import { BannerTypes } from '@gin-betting/banners';
import core, { Entities, GQL, View } from '@gin-betting/core';
import { Map, OrderedMap } from 'immutable';
import { map, reduce, transform } from 'lodash';

import { ExtendedRouts, MARKETS_ACTIVE_STATUSES } from 'app-constants';
import { ParentPath } from 'types';
import { getRouteBySportType } from 'utils';
import { getMissingMatches } from './actions';
import {
    ALL,
    FEATURED_SPORT_TYPE_MAPPING,
    FeaturedProviderConfigType,
    MARKET_LIMIT,
    ZONE_LOCATIONS,
} from './constants';
import type { FeaturedConfig, MatchIdsBySport, WidgetType } from './types';

const {
    utils: { getSportTypeBySport },
    constants: { idPathDivider },
} = core;

const checkIsCustomList = (
    item: GQL.GetFeaturedZoneConfigsQuery['featuredZoneConfigs'][number]
): item is {
    __typename: 'FeaturedZoneCustomListProvider';
} & GQL.FeaturedZoneCustomListProviderFragment => 'slug' in item;

const getRouteByFeaturedSportType = (
    featuredSportType: GQL.FeaturedSportType
): ParentPath => {
    const sportType = FEATURED_SPORT_TYPE_MAPPING[featuredSportType];

    return getRouteBySportType(sportType);
};

const getFeaturedConfig = (
    config: GQL.GetFeaturedZoneConfigsQuery['featuredZoneConfigs'][number],
    allSports: Map<string, View.Sport>
): FeaturedConfig | null => {
    if (checkIsCustomList(config)) {
        const route = getRouteByFeaturedSportType(config.sportType);

        return {
            type: FeaturedProviderConfigType.CustomList,
            slug: config.slug,
            name: config.title,
            description: config.description,
            logo: config.image,
            priority: config.priority,
            linkToTournament: `${route}/${config.slug}`,
        };
    }

    if (!config.tournament) return null;

    const {
        priority,
        tournament: { slug, sportId, name, description, logo },
    } = config;
    const sport = allSports.get(sportId);

    if (!sport) return null;

    const sportType = getSportTypeBySport(sport);
    const route = getRouteBySportType(sportType);

    return {
        type: FeaturedProviderConfigType.Tournament,
        slug,
        name,
        description,
        logo,
        priority,
        linkToTournament: `${route}${ExtendedRouts.TOURNAMENT}${slug}`,
    };
};

export const getFeaturedConfigKey = (slug: string, type: string): string =>
    `${slug}${idPathDivider}${type}`;

export const getTransformedFeaturedConfigs = (
    featuredZoneConfigs: GQL.GetFeaturedZoneConfigsQuery['featuredZoneConfigs'],
    allSports: OrderedMap<string, View.Sport>
): OrderedMap<string, FeaturedConfig> => {
    return featuredZoneConfigs.reduce((acc, config) => {
        const transformedConfig = getFeaturedConfig(config, allSports);

        if (!transformedConfig?.slug) return acc;

        const key = getFeaturedConfigKey(
            transformedConfig.slug,
            transformedConfig.type
        );

        return acc.set(key, transformedConfig);
    }, OrderedMap<string, FeaturedConfig>());
};

export function groupBannerItemsBySport(
    bannerItems: BannerTypes.BannerSportEvent[],
    defaultAcc?: MatchIdsBySport
): MatchIdsBySport {
    return reduce(
        bannerItems,
        (acc, { group, id }) => {
            const sportId = group?.groupBy ?? ALL;

            acc[sportId] = (acc[sportId] || new Set<string>()).add(id);

            return acc;
        },
        defaultAcc ?? {}
    );
}

type GetAttributesByPeriodArgs = { [key in WidgetType]: number };

export const getAttributesByPeriod = (
    topMatchListIndexConfig: GetAttributesByPeriodArgs
): { [key: string]: BannerTypes.BannerZoneAttributes[] } => {
    return transform(topMatchListIndexConfig, (acc, index, type) => {
        acc[type] = map(ZONE_LOCATIONS, pageLocation => ({
            index,
            pageLocation,
        }));
    });
};

export const makeGetMissingMatches = (
    sportEventsIds: string[]
): Entities.MakeGetMissingMatchesResult<{}> =>
    getMissingMatches({
        sportEventsIds,
        isTopMarkets: true,
        marketLimit: MARKET_LIMIT,
        withMarketsCount: true,
        marketStatuses: MARKETS_ACTIVE_STATUSES,
    });
